import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Crown, Loader2, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { usePlatform } from "@/lib/platform";

const RETURN_KEY = "aetryx_return";
const COPY = {
  premium: ["Sign in to watch", "is part of the full AETRYX catalog. Sign up free, then upgrade to unlock every Original."],
  library: ["Your library lives in your account", "Sign in to see your list, history and downloads."],
  mylist: ["Save it to My List", "Sign in to build your list across every device."],
  remind: ["Never miss a premiere", "Sign in and we'll notify you the moment it goes live."],
  create: ["Create Studio needs an account", "Sign in to write, cast and storyboard your own movies."],
  account: ["Your AETRYX account", "Sign in to manage profiles, plan and settings."],
  pricing: ["Create your account first", "Sign up free, then pick the plan that fits you."],
  nudge: ["Save your progress", "Sign up free to keep your place and unlock all AETRYX Originals."],
  locked: ["Sign in to continue", "Save your progress and unlock all AETRYX Originals — sign up free."],
};

const Field = ({ testId, ...props }) => (
  <input data-testid={testId} {...props} className="w-full rounded-xl border border-[#D4AF37]/25 bg-black/50 px-4 py-3 text-sm text-white placeholder:text-[#5A5A62] outline-none transition-colors focus:border-[#FFD86A]" />
);

const RegClosed = ({ mode }) => (
  <p data-testid="auth-modal-reg-closed" className="rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06] px-4 py-3 text-sm text-white/75">
    {mode === "invite_only" ? "AETRYX is invite-only right now — use your invite link to join." : "New sign-ups are closed right now. Existing members can still sign in."}
  </p>
);

export const AuthModal = ({ state, onClose }) => {
  const { login, register } = useAuth();
  const platform = usePlatform();
  const location = useLocation();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const regOpen = platform.registrations_open !== false;
  useEffect(() => { if (state) { setMode(state.mode === "register" && regOpen ? "register" : "login"); setError(""); } }, [state, regOpen]);

  const [head, sub] = COPY[state?.reason] || COPY.locked;
  const remember = () => sessionStorage.setItem(RETURN_KEY, state?.returnTo || `${location.pathname}${location.search}`);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true); remember();
    try {
      if (mode === "register") await register(form.name, form.email, form.password);
      else await login(form.email, form.password);
      onClose();
    } catch (err) {
      sessionStorage.removeItem(RETURN_KEY);
      setError(formatApiError(err));
    } finally { setBusy(false); }
  };
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const google = () => { remember(); window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/profiles")}`; };
  const isReg = mode === "register";

  return (
    <Dialog open={Boolean(state)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="auth-modal" className="max-w-md overflow-hidden border border-[#D4AF37]/40 bg-[#050505] p-0 text-white shadow-[0_0_80px_rgba(212,175,55,0.18)]">
        <div className="relative h-28 overflow-hidden">
          {state?.title?.backdrop_url && <img src={state.title.backdrop_url} alt="" className="absolute inset-0 h-full w-full object-cover opacity-45" />}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.22),transparent_70%)]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent" />
          <span className="absolute left-1/2 top-1/2 grid h-14 w-14 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-[#FFD86A]/70 bg-black/70 text-[#FFD86A] shadow-[0_0_40px_rgba(212,175,55,0.45)]">
            {state?.reason === "premium" ? <Lock className="h-6 w-6" /> : <Crown className="h-6 w-6" />}
          </span>
        </div>
        <div className="px-7 pb-7 -mt-1">
          <DialogTitle data-testid="auth-modal-title" className="text-center font-display text-2xl font-bold gold-text">{head}</DialogTitle>
          <DialogDescription className="mt-2 text-center text-sm text-white/65">
            {state?.reason === "premium" && state?.title?.title ? <><span className="text-[#FFD86A]">{state.title.title}</span> {sub}</> : sub}
          </DialogDescription>
          <div className="mt-5 grid grid-cols-2 rounded-full border border-[#D4AF37]/25 p-1 text-sm font-semibold">
            {[["login", "Sign in"], ["register", "Sign up"]].map(([m, l]) => (
              <button key={m} type="button" data-testid={`auth-modal-tab-${m}`} onClick={() => { setMode(m); setError(""); }} className={`rounded-full py-2 transition-colors ${mode === m ? "bg-gradient-to-r from-[#FFE9A3] via-[#F5C451] to-[#D4AF37] text-[#050505]" : "text-white/70 hover:text-[#FFD86A]"}`}>{l}</button>
            ))}
          </div>
          {isReg && !regOpen ? <div className="mt-5"><RegClosed mode={platform.registration_mode} /></div> : (
            <form onSubmit={submit} className="mt-5 space-y-3" data-testid="auth-modal-form">
              <button type="button" data-testid="auth-modal-google" onClick={google} className="flex w-full items-center justify-center gap-2 rounded-full bg-white py-2.5 text-sm font-bold text-black hover:bg-white/90 transition-colors">Continue with Google</button>
              {isReg && <Field testId="auth-modal-name" value={form.name} onChange={set("name")} placeholder="Your name" autoComplete="name" />}
              <Field testId="auth-modal-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" autoComplete="email" />
              <Field testId="auth-modal-password" type="password" required minLength={6} value={form.password} onChange={set("password")} placeholder="Password" autoComplete={isReg ? "new-password" : "current-password"} />
              {error && <p data-testid="auth-modal-error" className="rounded-lg border border-[#E50914]/40 bg-[#E50914]/10 px-3 py-2 text-sm text-[#ff8a8a]">{error}</p>}
              <button type="submit" disabled={busy} data-testid="auth-modal-submit" className="btn-gold w-full py-3 text-sm disabled:opacity-70">{busy && <Loader2 className="h-4 w-4 animate-spin" />} {isReg ? "Sign up free" : "Sign in"}</button>
            </form>
          )}
          <button data-testid="auth-modal-close" onClick={onClose} className="mt-4 block w-full text-center text-sm text-[#8B8B8B] hover:text-white transition-colors">Keep browsing</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
